import { Injectable, Input, OnInit } from '@angular/core';
// An RxJS BehaviorSubject is used to provide a stream of authentication status events that we can subscribe to anywhere in the app
import { Router } from '@angular/router';
// support creating an observable token expiration timer
import { BehaviorSubject, Subscription, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AUTH_CONFIG } from './auth.config';
import * as auth0 from 'auth0-js';
import { ENV } from './../core/env.config';

@Injectable()


export class AuthService implements OnInit {
//  Get rid of Angular 10 error:
// Property 'loggedIn' is used before its initialization.
@Input() loggedIn: boolean;
@Input() loggedIn$: any;
// loggedIn$: any;

  // Create Auth0 web auth instance
  private Auth0 = new auth0.WebAuth({
    clientID: AUTH_CONFIG.CLIENT_ID,
    domain: AUTH_CONFIG.CLIENT_DOMAIN,
    responseType: 'token',
    redirectUri: AUTH_CONFIG.REDIRECT,
    audience: AUTH_CONFIG.AUDIENCE,
    scope: AUTH_CONFIG.SCOPE
  });
  accessToken: string;
  userProfile: any;
  expiresAt: number;
  // Create a stream of logged in status to communicate throughout app
  // Angular 10 error:
  // Property 'loggedIn' is used before its initialization.
  // loggedIn: boolean;
  // loggedIn$ = new BehaviorSubject<boolean>(this.loggedIn);
  loggingIn: boolean;
  // store the user's admin status
  isAdmin: boolean;
  // Subscribe to token expiration stream
  refreshSub: Subscription; // property for subscription to token expiration stream
  routeSub: Subscription;

  constructor(private router: Router) {
    // If app auth token is not expired, request new token
    if (JSON.parse(localStorage.getItem('expires_at')) > Date.now()) {
      this.renewToken();
    }
  }

  ngOnInit(): void{
    this.loggedIn$ = new BehaviorSubject<boolean>(this.loggedIn);
    // this.loggedIn$ = this.loggedIn;
  }

  setLoggedIn(value: boolean) {
    // Update login status subject
    // this.loggedIn$.next(value);
    this.loggedIn = value;
  }

  login() {
    // Auth0 authorize request
    this.Auth0.authorize();
  }

  handleAuth() {
    // When Auth0 hash parsed, get profile
    this.Auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken) {
        window.location.hash = '';
        this._getProfile(authResult);
      } else if (err) {
        console.error(`Error authenticating: ${err.error}`);
        this._clearRedirect();
        this.router.navigate(['/']);
        console.error(`Error authenticating: ${err.error}`);
      }
      // this.router.navigate(['/']);
    });
  }

  private _getProfile(authResult) {
    this.loggingIn = true;
    // Use access token to retrieve user's profile and set session
    this.Auth0.client.userInfo(authResult.accessToken, (err, profile) => {
      if (profile) {
        this._setSession(authResult, profile);
        this.router.navigate(['']);
       // this._redirect();
      } else if (err) {
        console.warn(`Error retrieving profile: ${err.error}`);
      }
    });
  }

  // navigates to the stored redirect URL (or as a failsafe, to the homepage)
  // then clear the redirect from local storage => ensure no lingering data left behind
  private _redirect() {
     // Redirect with or without 'tab' query parameter
    // Note: does not support additional params besides 'tab'
    // assess the authRedirect string stored in local storage and split it into
    // the appropriate Angular path and query parameters
    const fullRedirect = decodeURI(localStorage.getItem('authRedirect'));
    // const redirectArr = fullRedirect.split('?tab=');
    // const navArr = [redirectArr[0] || '/'];
    // const tabObj = redirectArr[1] ? { queryParams: { tab: redirectArr[1] }} : null;

    // if (!tabObj) {
    //   this.router.navigate(navArr);
    // } else {
    //   this.router.navigate(navArr, tabObj);
      
    // }

    // // Redirection completed; clear redirect from storage
    // this._clearRedirect();
    this.router.navigate(['/']);
  }

  // // removes the authRedirect item from local storage
  private _clearRedirect() {
    // Remove redirect from localStorage
    localStorage.removeItem('authRedirect');
  }


  private _setSession(authResult, profile?) {
    this.expiresAt = (authResult.expiresIn * 1000) + Date.now();
    // Store expiration in local storage to access in constructor
    localStorage.setItem('expires_at', JSON.stringify(this.expiresAt));
    this.accessToken = authResult.accessToken;
    if (profile) {
      this.userProfile = profile;
      this.isAdmin = this._checkAdmin(profile);
    }
    // Update login status in loggedIn$ stream
    this.setLoggedIn(true);
    this.loggingIn = false;
    // Schedule access token renewal => If getting new token, also get user data again
    this.scheduleRenewal();
  }

  private _checkAdmin(profile) {
    // Check if the user has admin role
    const roles = profile[AUTH_CONFIG.NAMESPACE] || [];
    return roles.indexOf('admin') > -1;
  }

  private _clearExpiration() {
    // Remove token expiration from localStorage
    localStorage.removeItem('expires_at');
  }

  logout() {
    // Remove data from localStorage
    this._clearExpiration();
    this._clearRedirect();
    // End Auth0 authentication session
    this.Auth0.logout({
      clientId: AUTH_CONFIG.CLIENT_ID,
      returnTo: ENV.BASE_URI
    });
  }

  get tokenValid(): boolean {
    // Check if current time is past access token's expiration
    return Date.now() < JSON.parse(localStorage.getItem('expires_at'));
  }

  renewToken() {
    // Check for valid Auth0 session
    // acquire new tokens w/ checkSession => uses postMessage under the hood
    // implement cross-origin communication between parent app & silent auth
    //  taking place in an iframe
    //
    this.Auth0.checkSession({}, (err, authResult) => {
      if (authResult && authResult.accessToken) {
        this._getProfile(authResult);
      } else {
        this._clearExpiration();
      }
    });
  }

  scheduleRenewal() {
    // If last token is expired, do nothing
    if (!this.tokenValid) { return; }
    // Unsubscribe from previous expiration observable
    this.unscheduleRenewal();
    // Create and subscribe to expiration observable
    // ie., If user is authenticated => do cleanup check => then create an observable expiresIn$
    const expiresIn$ = of(this.expiresAt).pipe(
      mergeMap( // flatten the stream
        expires => {
          const now = Date.now();
          // Use timer to track delay until expiration
          // to run the refresh at the proper time
          return timer(Math.max(1, expires - now)); // produces a value (0) when current access token expires
        }
      )
    );

    this.refreshSub = expiresIn$
      .subscribe(
        () => {
          this.renewToken();
          this.scheduleRenewal();
        }
      );
  }

  unscheduleRenewal() {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
    }
  }


}
